import { useEffect, useRef } from 'react';

/** A short click keeps navigation/filtering; holding for 450 ms starts a drag. */
export function useHoldReorder(selector: string, ids: number[], enabled: boolean, onOrder: (ids: number[]) => void) {
  const current = useRef({ ids, onOrder });
  useEffect(() => { current.current = { ids, onOrder }; }, [ids, onOrder]);
  useEffect(() => {
    if (!enabled) return;
    let press: { id: number; x: number; y: number; node: HTMLElement; pointer: number } | null = null;
    let active = false, timer = 0, suppressUntil = 0;
    let target: { id: number; after: boolean; node: HTMLElement } | null = null;
    const clearMarker = () => { target?.node.classList.remove('sc-drop-before', 'sc-drop-after'); target = null; };
    const cancel = () => { clearTimeout(timer); press?.node.classList.remove('sc-dragging'); clearMarker(); press = null; active = false; document.body.classList.remove('sc-reordering'); };
    const down = (event: PointerEvent) => {
      if (event.button !== 0 || event.target instanceof HTMLElement && event.target.closest('.slick-resizable-handle')) return;
      const node = event.target instanceof Element ? event.target.closest<HTMLElement>(selector) : null;
      const id = Number(node?.dataset.structureId);
      if (!node || !current.current.ids.includes(id)) return;
      cancel(); press = { id, x: event.clientX, y: event.clientY, node, pointer: event.pointerId };
      timer = window.setTimeout(() => { if (!press) return; active = true; node.classList.add('sc-dragging'); document.body.classList.add('sc-reordering'); window.getSelection()?.removeAllRanges(); }, 450);
    };
    const move = (event: PointerEvent) => {
      if (!press || press.pointer !== event.pointerId) return;
      if (!active) { if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > 7) cancel(); return; }
      event.preventDefault();
      const node = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>(selector);
      const id = Number(node?.dataset.structureId);
      clearMarker();
      if (!node || id === press.id || !current.current.ids.includes(id)) return;
      const rect = node.getBoundingClientRect();
      target = { id, after: event.clientX > rect.left + rect.width / 2, node };
      node.classList.add(target.after ? 'sc-drop-after' : 'sc-drop-before');
    };
    const up = () => {
      if (active && press) {
        suppressUntil = Date.now() + 500;
        if (target) {
          const next = current.current.ids.filter(id => id !== press!.id);
          next.splice(next.indexOf(target.id) + (target.after ? 1 : 0), 0, press.id);
          if (next.join() !== current.current.ids.join()) current.current.onOrder(next);
        }
      }
      cancel();
    };
    const click = (event: MouseEvent) => { if (Date.now() < suppressUntil) { event.preventDefault(); event.stopImmediatePropagation(); } };
    const drag = (event: DragEvent) => { if (press) event.preventDefault(); };
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') { if (active) suppressUntil = Date.now() + 500; cancel(); } };
    document.addEventListener('pointerdown', down, true); document.addEventListener('pointermove', move, { capture: true, passive: false });
    document.addEventListener('pointerup', up, true); document.addEventListener('pointercancel', cancel, true); document.addEventListener('click', click, true);
    document.addEventListener('dragstart', drag, true); document.addEventListener('keydown', key); window.addEventListener('blur', cancel);
    return () => { cancel(); document.removeEventListener('pointerdown', down, true); document.removeEventListener('pointermove', move, true); document.removeEventListener('pointerup', up, true); document.removeEventListener('pointercancel', cancel, true); document.removeEventListener('click', click, true); document.removeEventListener('dragstart', drag, true); document.removeEventListener('keydown', key); window.removeEventListener('blur', cancel); };
  }, [selector, enabled]);
}
